import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { Observable, defer } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import * as moment from 'moment';
import 'moment/locale/es';

export type AppLang = 'es' | 'en';

const STORAGE_KEY = 'f2rt_lang';
const DEFAULT_LANG: AppLang = 'es';

/**
 * Servicio central de internacionalización (i18n) de VIVO.
 *
 * Unifica el cambio de idioma en un solo punto:
 *  - ngx-translate (textos de la app, claves en assets/i18n/{es,en}.json)
 *  - PrimeNG (etiquetas de tablas/calendarios vía setTranslation)
 *  - moment (locale de fechas)
 *  - persistencia de la preferencia en localStorage
 *
 * Se inicializa una vez desde AppComponent.ngOnInit().
 */
@Injectable({ providedIn: 'root' })
export class I18nService {
  private translate = inject(TranslateService);
  private primeng = inject(PrimeNG);

  /** Idiomas soportados (para el selector). */
  readonly langs: AppLang[] = ['es', 'en'];

  /** Inicializa el idioma desde la preferencia guardada (o el default). */
  init(): void {
    this.translate.addLangs(this.langs);
    // En v16 setDefaultLang sigue vigente (el rename a setFallbackLang es de v17).
    this.translate.setDefaultLang(DEFAULT_LANG);
    const saved = this.read();
    this.use(saved);
  }

  /** Idioma activo. */
  get current(): AppLang {
    return (this.translate.currentLang as AppLang) || DEFAULT_LANG;
  }

  // ============================================================
  // 🆕 INICIO: el idioma activo como FLUJO - 2026-09-17
  // ============================================================
  // Hallazgo del lider (tema 3 de la prueba guiada): al pasar a ingles «cambia
  // todo a ingles pero en la seleccion queda en espanol». Causa: los dos
  // selectores -el globo del encabezado y el de Mi cuenta- guardaban su propia
  // COPIA del idioma (el globo arma su menu en ngOnInit; el perfil fija
  // `selectedLanguage` en el constructor) y solo la refrescaban cuando el
  // cambio salia de ellos mismos. Cambiar desde uno dejaba al otro mintiendo.
  //
  // La fuente es `onLangChange` de ngx-translate, que se dispara venga el
  // cambio de donde venga. `defer` hace que cada suscriptor reciba primero el
  // idioma ACTUAL en el momento de suscribirse (con `startWith` suelto se
  // evaluaria una sola vez, al crear el observable).
  /** Idioma activo: emite al suscribirse y en cada cambio. */
  readonly lang$: Observable<AppLang> = defer(() =>
    this.translate.onLangChange.pipe(
      map(e => (e.lang as AppLang) || DEFAULT_LANG),
      startWith(this.current),
      distinctUntilChanged(),
    ),
  );
  // ============================================================
  // 🆕 FIN: el idioma activo como FLUJO
  // ============================================================

  /** Cambia el idioma en toda la app y lo persiste. */
  use(lang: AppLang): void {
    if (!this.langs.includes(lang)) lang = DEFAULT_LANG;
    this.translate.use(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    moment.locale(lang);
    this.primeng.setTranslation(PRIMENG_LABELS[lang]);
    // 🔧 2026-10-09 (QA del registro de personal): el idioma del documento sigue al de la app. index.html fija
    // lang="es" y nunca cambiaba: un lector de pantalla leia en espanol una pagina en ingles (WCAG 3.1.1) y el
    // navegador ofrecia traducirla.
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }

  /** Alterna entre español e inglés. */
  toggle(): void {
    this.use(this.current === 'es' ? 'en' : 'es');
  }

  private read(): AppLang {
    const v = localStorage.getItem(STORAGE_KEY) as AppLang;
    return this.langs.includes(v) ? v : DEFAULT_LANG;
  }
}

// Etiquetas de PrimeNG por idioma (calendario, filtros, mensajes vacíos, etc.).
// PrimeNG hace merge sobre los defaults, así que basta con lo localizable.
const PRIMENG_LABELS: Record<AppLang, any> = {
  es: {
    dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
    dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    today: 'Hoy',
    clear: 'Limpiar',
    weekHeader: 'Sem',
    firstDayOfWeek: 1,
    emptyMessage: 'No se encontraron resultados',
    emptyFilterMessage: 'No se encontraron resultados',
    apply: 'Aplicar',
    matchAll: 'Coincidir todo',
    matchAny: 'Coincidir cualquiera',
    contains: 'Contiene',
    startsWith: 'Comienza con',
    endsWith: 'Termina con',
    equals: 'Igual a',
    notEquals: 'Distinto de',
  },
  en: {
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    today: 'Today',
    clear: 'Clear',
    weekHeader: 'Wk',
    firstDayOfWeek: 0,
    emptyMessage: 'No results found',
    emptyFilterMessage: 'No results found',
    apply: 'Apply',
    matchAll: 'Match all',
    matchAny: 'Match any',
    contains: 'Contains',
    startsWith: 'Starts with',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
  },
};
